import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { href, Link } from '../app/router';
import { Button, buttonClass, linkClass } from './Button';
import { Logo } from './Logo';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';

/** The editor's frame while a board opens: paper-white dotted canvas, a top bar and a toolbar in outline (no raw text). */
export function BoardSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="os-appear relative h-full overflow-hidden bg-paper [background-image:radial-gradient(circle,#d4d4d8_1px,transparent_1.2px)] [background-size:22px_22px]">
      <span aria-hidden="true" className="absolute left-3 top-3 flex h-11 w-64 items-center gap-2 rounded-2xl bg-chrome px-3 shadow-float">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-4 w-32" />
      </span>
      <span aria-hidden="true" className="absolute bottom-4 left-1/2 flex h-13 w-[min(36rem,calc(100%-2rem))] -translate-x-1/2 items-center gap-2 rounded-2xl bg-chrome px-3 shadow-float">
        {Array.from({ length: 9 }, (_, i) => (
          <Skeleton key={i} className="h-8 w-8 rounded-lg" />
        ))}
      </span>
    </div>
  );
}

/**
 * A page of the dashboard while its data loads: a title and a grid of cards in outline. It copies the real page
 * (features/dashboard/cards.tsx: `Section` heading, `Grid`, `BoardCard`) down to the card box (`rounded-2xl bg-chrome
 * shadow-float`, a 16:10 preview, a title row and a meta row), so the content takes the skeleton's place without a jump.
 */
export function PageSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="os-appear flex flex-col gap-4">
      <div aria-hidden="true" className="flex h-[1.625rem] items-center">
        <Skeleton className="h-5 w-48" />
      </div>
      <div aria-hidden="true" className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-6">
        {Array.from({ length: 6 }, (_, i) => (
          <span key={i} className="flex flex-col gap-2.5 rounded-2xl bg-chrome p-2 pb-3 shadow-float">
            <Skeleton className="aspect-[16/10] w-full rounded-xl" />
            <span className="flex h-8 items-center px-1 pointer-coarse:h-11">
              <Skeleton className="h-3.5 w-3/4" />
            </span>
            <span className="flex h-4 items-center px-1">
              <Skeleton className="h-3 w-1/3" />
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Signed-in state is being checked, an invitation or a shared link is opening: the studio's hand, a ring and a line. */
export function WaitingScreen({ message }: { message?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" className="os-appear flex h-dvh flex-col items-center justify-center gap-4 bg-bg px-6 text-center font-sans text-fg-muted">
      <Logo size={40} decorative className="text-fg" />
      <Spinner decorative className="text-fg-muted" />
      <p className="text-sm">{message ?? t('p18.e.waiting')}</p>
    </div>
  );
}

/** An address that leads nowhere: one heading, a way home and a way to what was open lately. */
export function NotFoundScreen() {
  const { t } = useTranslation();
  return (
    <main className="os-appear flex h-dvh flex-col items-center justify-center gap-4 bg-bg px-6 text-center font-sans text-fg">
      <Logo size={40} decorative />
      <h1 className="font-display text-display-md font-normal tracking-display">{t('p18.e.notFoundTitle')}</h1>
      <p className="max-w-sm text-sm text-fg-muted">{t('p18.e.notFoundBody')}</p>
      <div className="mt-2 flex flex-col items-center gap-3">
        <Link to={href('home')} className={buttonClass('primary')}>
          {t('p18.e.notFoundHome')}
        </Link>
        <Link to={href('recent')} className={linkClass}>
          {t('p18.e.notFoundRecent')}
        </Link>
      </div>
    </main>
  );
}

export function EmptyState({ icon, title, body, action }: { icon?: ReactNode; title: string; body?: string; action?: ReactNode }) {
  return (
    <div data-empty-state className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-10 text-center font-sans">
      {icon && <span className="text-fg-muted">{icon}</span>}
      <p className="text-sm font-medium text-fg">{title}</p>
      {body && <p className="max-w-sm text-sm text-fg-muted">{body}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ title, body, onRetry, retryLabel, children }: { title: string; body?: string; onRetry?: () => void; retryLabel?: string; children?: ReactNode }) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-start gap-3 px-6 py-10 font-sans text-fg">
      {/* Only the message is announced; the actions follow it. */}
      <div role="alert" className="flex flex-col gap-1">
        <p className="font-display text-title font-semibold">{title}</p>
        {body && <p className="text-sm text-fg-muted">{body}</p>}
      </div>
      {(onRetry || children) && (
        <div className="flex flex-wrap gap-2">
          {onRetry && retryLabel && (
            <Button variant="primary" onClick={onRetry}>
              {retryLabel}
            </Button>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
